/**
 * True when pdf.js failed because `fetch` never delivered an http response.
 *
 * `fetch` rejects with a `TypeError` for every such failure and does not say why: the page
 * navigated away while the document was still loading, the connection dropped, the device is
 * offline. None of these is a fault on our side, so PdfPreview does not report them.
 *
 * The wording differs per engine ("Load failed" in WebKit, "Failed to fetch" in Chromium,
 * "NetworkError when attempting to fetch resource." in Firefox), so the check is on the type.
 * The pdf.js worker wraps a foreign error as `UnknownErrorException`, a class it does not
 * export, with the original error's `toString()` in `details`; that is where the type name is.
 *
 * An http error status is a response, so it arrives as `ResponseException` and is still
 * reported, as is a corrupt document (`InvalidPDFException`) and a worker error of another type.
 */
export function isPdfFetchFailure(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === "UnknownErrorException" &&
    "details" in error &&
    typeof error.details === "string" &&
    error.details.startsWith("TypeError:")
  );
}
